import { type UseFormReturn, Controller } from "react-hook-form";
import { AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { MultiSelect } from "@/components/ui/MultiSelect";
import { DatePicker } from "@/components/ui/DatePicker";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import type { IntakeFormData, DropdownConfig } from "../hooks/useIntakeForm";

interface BackgroundStepProps {
  form: UseFormReturn<IntakeFormData>;
  dropdowns: DropdownConfig | undefined;
  isLoadingDropdowns: boolean;
}

export function BackgroundStep({
  form,
  dropdowns,
  isLoadingDropdowns,
}: BackgroundStepProps) {
  const {
    register,
    control,
    watch,
    formState: { errors },
  } = form;

  if (isLoadingDropdowns) {
    return <BackgroundStepSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Abuse Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Abuse Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Controller
            name="abuseTypes"
            control={control}
            render={({ field }) => (
              <MultiSelect
                label="Type of Abuse"
                options={dropdowns?.abuseTypes ?? []}
                value={field.value}
                onChange={field.onChange}
                columns={3}
                error={errors.abuseTypes?.message}
              />
            )}
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Controller
              name="relationshipToAbuser"
              control={control}
              render={({ field }) => (
                <Select
                  label="Relationship to Abuser"
                  options={dropdowns?.relationships ?? []}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  searchable
                  clearable
                  loading={isLoadingDropdowns}
                />
              )}
            />
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                Abuse Status
              </label>
              <div className="flex items-center gap-4 pt-1.5">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    {...register("isAbuseCurrent")}
                    className="h-4 w-4 rounded accent-primary"
                  />
                  Currently in abusive situation
                </label>
              </div>
            </div>
            <Controller
              name="abuseStartDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="Abuse Start Date"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  max={new Date().toISOString().slice(0, 10)}
                />
              )}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Weapons Used"
              {...register("weaponsUsed")}
            />
            <Input
              label="Weapons Threatened"
              {...register("weaponsThreatened")}
            />
          </div>

          <Input
            label="Length of Time with Abuser"
            placeholder="e.g., 5 years"
            {...register("lengthWithAbuser")}
          />
        </CardContent>
      </Card>

      {/* Protection & Safety */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Safety & Protection</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  {...register("hasProtectionOrder")}
                  className="h-4 w-4 rounded accent-primary"
                />
                Has Protection Order
              </label>
              {watch("hasProtectionOrder") && (
                <Controller
                  name="protectionOrderExpiration"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Protection Order Expiration"
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    />
                  )}
                />
              )}
            </div>
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  {...register("safetyPlanCompleted")}
                  className="h-4 w-4 rounded accent-primary"
                />
                Safety Plan Completed
              </label>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">
                  Danger Assessment Score
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    {...register("dangerAssessmentScore", { valueAsNumber: true })}
                    className="flex h-9 w-24 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                  <span className="text-sm text-muted-foreground">/ 100</span>
                </div>
              </div>
              <label className={cn(
                "flex items-center gap-2 text-sm",
                watch("isHighDanger") && "font-semibold text-destructive",
              )}>
                <input
                  type="checkbox"
                  {...register("isHighDanger")}
                  className="h-4 w-4 rounded accent-destructive"
                />
                <AlertTriangle className="h-4 w-4" />
                High Danger
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Background Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Background Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                {...register("hasChildren")}
                className="h-4 w-4 rounded accent-primary"
              />
              Has Children
            </label>
            {watch("hasChildren") && (
              <Input
                label="Number of Children"
                type="number"
                min={0}
                {...register("numberOfChildren", { valueAsNumber: true })}
              />
            )}
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                {...register("isPregnant")}
                className="h-4 w-4 rounded accent-primary"
              />
              Pregnant
            </label>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                {...register("hasDisability")}
                className="h-4 w-4 rounded accent-primary"
              />
              Has Disability
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                {...register("isVeteran")}
                className="h-4 w-4 rounded accent-primary"
              />
              Veteran
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                {...register("isHomeless")}
                className="h-4 w-4 rounded accent-primary"
              />
              Homeless
            </label>
          </div>

          {watch("hasDisability") && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                Disability Details
              </label>
              <textarea
                {...register("disabilityDetails")}
                rows={3}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="Describe disabilities..."
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sensitive Fields */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Chemical Dependence & Medications</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">
                Chemical Dependence
              </label>
              <label className="flex items-center gap-1.5 text-xs text-amber-600">
                <input
                  type="checkbox"
                  {...register("chemicalDependenceSensitive")}
                  className="h-3.5 w-3.5 rounded accent-amber-600"
                />
                Sensitive
              </label>
            </div>
            <textarea
              {...register("chemicalDependence")}
              rows={3}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder="Describe chemical dependence history..."
            />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">
                Medications
              </label>
              <label className="flex items-center gap-1.5 text-xs text-amber-600">
                <input
                  type="checkbox"
                  {...register("medicationsSensitive")}
                  className="h-3.5 w-3.5 rounded accent-amber-600"
                />
                Sensitive
              </label>
            </div>
            <textarea
              {...register("medications")}
              rows={3}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder="List current medications..."
            />
          </div>
        </CardContent>
      </Card>

      {/* Plans */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Plans & Presenting Situation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">
              Presenting Situation
            </label>
            <textarea
              {...register("presentingSituation")}
              rows={4}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder="Describe the presenting situation..."
            />
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                Plans Regarding Abuser
              </label>
              <textarea
                {...register("plansAbuser")}
                rows={3}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                Plans Regarding Children
              </label>
              <textarea
                {...register("plansChildren")}
                rows={3}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                Plans Regarding Income
              </label>
              <textarea
                {...register("plansIncome")}
                rows={3}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function BackgroundStepSkeleton() {
  return (
    <div className="space-y-6">
      {[1, 2, 3].map((i) => (
        <Card key={i}>
          <CardHeader>
            <div className="h-5 w-40 animate-pulse rounded bg-muted" />
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {[1, 2].map((j) => (
                <div key={j} className="space-y-1.5">
                  <div className="h-4 w-24 animate-pulse rounded bg-muted" />
                  <div className="h-9 w-full animate-pulse rounded-md bg-muted" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
